// Owns "current room" state. Stateless wrt rendering — emits events that App listens to.

import type { Room, RoomsConfig } from '../app/types';
import { Emitter } from '../app/emitter';

export type RoomChangeTrigger = 'init' | 'navigation' | 'minimap' | 'api' | 'vr';

export interface RoomChangeEvent {
  previous: Room | null;
  current: Room;
  trigger: RoomChangeTrigger;
}

export class RoomManager {
  private readonly config: RoomsConfig;
  private readonly emitter = new Emitter<RoomChangeEvent>();
  private current: Room;

  constructor(config: RoomsConfig) {
    if (config.rooms.length === 0) throw new Error('rooms.json has no rooms');
    const def = config.rooms.find((r) => r.id === config.defaultRoomId) ?? config.rooms[0]!;
    this.config = config;
    this.current = def;
  }

  getCurrent(): Room {
    return this.current;
  }

  getAll(): readonly Room[] {
    return this.config.rooms;
  }

  getById(id: string): Room | null {
    return this.config.rooms.find((r) => r.id === id) ?? null;
  }

  setRoom(id: string, trigger: RoomChangeTrigger = 'api'): boolean {
    const next = this.getById(id);
    if (!next || next.id === this.current.id) return false;
    const previous = this.current;
    this.current = next;
    this.emitter.emit({ previous, current: next, trigger });
    return true;
  }

  /**
   * Used at init time: emit a synthetic change event so subscribers (UI, panorama,
   * hotspots) can perform their first render in a single code path.
   */
  fireInitial(): void {
    this.emitter.emit({ previous: null, current: this.current, trigger: 'init' });
  }

  on(listener: (e: RoomChangeEvent) => void): () => void {
    return this.emitter.on(listener);
  }
}
